import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InfoIcon from '@mui/icons-material/Info';
import FrequencyMetrics from '../../../../components/olymp/FrequencyMetrics';
import { getParameterLookupId } from '../../../../services/catalogService';
import DataMappingCard from './DataMappingCard';
import WerteverteilungCard from './WerteverteilungCard';
import WertekennzahlenCard from './WertekennzahlenCard';
import HistogrammCard from './HistogrammCard';
import {
  getOMOPStatusColor,
  getOMOPStatusLabel,
  getOmopAbbildbarkeit,
  getOpenEhrPrimaryName,
  getAqlPfade,
  getOmopFragmentierung,
} from '../utils/helpers';
import { useTranslation } from 'react-i18next';

/**
 * Expanded Row Component
 * Displays detailed information when a table row is expanded
 *
 * @param {Object} param - Parameter data
 * @param {Object} parameterLists - All parameter lists
 * @param {Array} activeWorkingList - Active working list IDs
 * @param {Function} handleOpenListDialog - Handler for opening list dialog
 */
/** Prozent lesbar: der Contract liefert Fliesskommazahlen wie 93.6349..., die
 *  Karte zeigt eine Nachkommastelle. `toFixed` haette bei einer Ganzzahl eine
 *  ueberfluessige Null angehaengt. */
const formatiereProzent = (wert) =>
  Number.isFinite(wert) ? String(Math.round(wert * 10) / 10) : '';

/** Ab diesem Anteil ohne erfasste Einheit gilt eine Zahlenstatistik als
 *  eingeschraenkt (#264). Ohne belastbare Zahlen zur Verteilung "haeufig
 *  vs. selten fehlend" ist das eine Annahme -- konservativ gewaehlt: klein
 *  genug, dass ein vereinzelter Ausreisser (ein einzelner Messwert ohne
 *  Einheit unter hunderten) keinen Hinweis ausloest, aber nicht so klein,
 *  dass er bei jedem gemischten Parameter aufblinkt. */
const SCHWELLE_OHNE_EINHEIT_PROZENT = 5;

/**
 * Der openEHR-Abschnitt der Detailansicht (#772 AK2, AK3).
 *
 * Zeigt, was die OMOP-Karte nicht zeigen kann: den Archetyp, die AQL-Pfade,
 * unter denen der Wert in einer EHRbase steht -- und ob es dazu ueberhaupt
 * eine OMOP-Entsprechung gibt.
 *
 * Gemessen an den 37 MIMIC-Templates aus `dsl2ehr`: von 51 belegten
 * Archetypen kennt OMOCL 39, davon haben **10 keine OMOP-Entsprechung**
 * (25,64 %). Die uebrigen 12 kennt OMOCL gar nicht -- das ist ein dritter
 * Fall und keine Messung, deshalb steht dafuer hier "unbekannt" und nicht
 * "nicht abbildbar".
 */
export const OpenEhrAbschnitt = ({ param, t }) => {
  const abbildbarkeit = getOmopAbbildbarkeit(param);
  const archetyp = getOpenEhrPrimaryName(param);
  const pfade = getAqlPfade(param);
  const fragmentierung = getOmopFragmentierung(param);

  return (
    <Box sx={{ mt: 3, p: 2, backgroundColor: 'white', borderRadius: 1, boxShadow: 1 }}>
      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, color: 'text.secondary' }}>
        {t('dataCatalog.expanded.openehrMapping')}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
        <Chip
          label={getOMOPStatusLabel(param.openehrStatus, t)}
          color={getOMOPStatusColor(param.openehrStatus)}
          size="small"
          sx={{ fontWeight: 600 }}
        />
        {archetyp ? (
          <>
            {param.openehrName && <Typography variant="body2">{param.openehrName}</Typography>}
            {param.openehrCode && (
              <Typography
                variant="caption"
                sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
              >
                {param.openehrCode}
              </Typography>
            )}
          </>
        ) : (
          <Typography variant="body2" color="textSecondary" fontStyle="italic">
            {t('dataCatalog.openehr.keinArchetyp')}
          </Typography>
        )}
      </Box>

      {pfade.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="textSecondary" fontWeight="bold" display="block">
            {t('dataCatalog.openehr.aqlPfade')}
          </Typography>
          {pfade.map((pfad) => (
            <Typography
              key={pfad}
              variant="caption"
              display="block"
              sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
            >
              {pfad}
            </Typography>
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        {abbildbarkeit === 'not_mappable' && (
          <Chip
            label={t('dataCatalog.openehr.nichtInOmopAbbildbar')}
            color="warning"
            size="small"
            sx={{ fontWeight: 600 }}
          />
        )}
        {abbildbarkeit === 'mappable' && param.omopCode && (
          <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'monospace' }}>
            {t('dataCatalog.openehr.entsprichtOmop', { code: param.omopCode })}
          </Typography>
        )}
        {abbildbarkeit === 'unassessed' && (
          <Typography variant="body2" color="textSecondary" fontStyle="italic">
            {t('dataCatalog.openehr.abbildbarkeitUnbekannt')}
          </Typography>
        )}
        {fragmentierung && (
          <Chip
            label={t('dataCatalog.openehr.fragmentierung', { anzahl: fragmentierung })}
            size="small"
            variant="outlined"
          />
        )}
      </Box>
    </Box>
  );
};

const ExpandedRow = ({
  param,
  parameterLists,
  activeWorkingList,
  handleOpenListDialog,
  loading = false,
  error = null,
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <Box sx={{ py: 3, px: 2, backgroundColor: '#f5f5f5' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="textSecondary">
            {t('dataCatalog.status.detailLoading')}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 3, px: 2, backgroundColor: '#f5f5f5' }}>
        <Alert severity="warning">{error}</Alert>
      </Box>
    );
  }

  // Numerisch oder kategorial? Das Kriterium ist die vorhandene
  // Zahlenstatistik (#244) -- nicht das Aussehen der Werte. `ABP Alarm [Low]`
  // traegt `90, 85, 80` als Text und hat trotzdem einen Median; wer nach dem
  // Aussehen entscheidet, greift genau dort daneben.
  const messmuster = param.frequencyMetrics?.measurementPattern || null;
  const istNumerisch = messmuster?.medianValue != null;

  // Anteil der Werte ohne erfasste Einheit (AK2, #264): `unit === null` ist im
  // Contract die nicht erfasste Einheit, keine echte Auspraegung. Fehlt der
  // Anteil selbst (kein `percentage` am Eintrag), bleibt die Statistik
  // unmarkiert -- eine Behauptung ueber einen Anteil, den niemand kennt, waere
  // keine konservative Annahme.
  const einheitOhneAngabe = (param.units || []).find((u) => u.unit == null);
  const anteilOhneEinheit = Number.isFinite(einheitOhneAngabe?.percentage)
    ? einheitOhneAngabe.percentage
    : null;
  const statistikEingeschraenkt = anteilOhneEinheit != null && anteilOhneEinheit > SCHWELLE_OHNE_EINHEIT_PROZENT;

  // Die "Standard"-Einheit ist die, an der sich andere Schreibweisen
  // normalisieren -- nie die nicht erfasste Einheit (#264, Rueckmeldung Runde
  // 1). `param.units` ist nach Anteil sortiert; traegt die nicht erfasste
  // Einheit den groessten Anteil, stuende sie sonst auf Index 0 und wuerde
  // als Normalisierungsziel ausgezeichnet -- das Gegenteil von AK1, das genau
  // diese Einheit als unbekannt kennzeichnen soll.
  const standardIndex = (param.units || []).findIndex((u) => u.unit != null);

  // Nur *erfasste* Einheiten zaehlen als "mehrere Einheiten" (#264,
  // Rueckmeldung Runde 2): bei `['kg', null]` gibt es eine bekannte Einheit
  // und einen nicht erfassten Anteil, keine zwei Auspraegungen, zwischen
  // denen normalisiert wuerde. `param.units.length > 1` zaehlte den nicht
  // erfassten Anteil mit und zeigte "alle Werte werden in kg konvertiert"
  // ausgerechnet dort, wo der Hinweis "12.5 % ohne erfasste Einheit" bereits
  // sagt, dass eben nicht alle Werte kg sind.
  const erfassteEinheitenAnzahl = (param.units || []).filter((u) => u.unit != null).length;

  return (
    <Box sx={{ py: 3, px: 2, backgroundColor: '#f5f5f5' }}>
      {/* Frequency & Availability Section */}
      {param.frequencyMetrics && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, px: 1 }}>
            <Typography variant="h6" fontWeight="bold">
              {t('dataCatalog.frequency.sectionTitle')}
            </Typography>
            {param.availableSince && (
              <Chip
                label={t('dataCatalog.frequency.availableSince', { date: param.availableSince })}
                size="small"
                variant="outlined"
                color="primary"
              />
            )}
          </Box>
          <FrequencyMetrics
            data={param.frequencyMetrics}
            availableSince={param.availableSince}
            sensitivity={param.sensitivity}
            usagePattern={param.usagePattern}
          />
        </Box>
      )}

      {/* Data Mapping Section */}
      {param.dataMapping && (
        <Box sx={{ mb: 2 }}>
          <DataMappingCard
            dataMapping={param.dataMapping}
            code={param.sourceCode || param.displayData?.sourceCode || ''}
            originalName={param.name}
          />
        </Box>
      )}

      {/* Werteverteilung (#215) -- direkt vor den Einheiten, weil beide
          dieselbe Frage beantworten: was steht in dieser Spalte?

          **Seit #244 in Kurzform, und bei Zahlen gar nicht.** Der aufgeklappte
          Bereich ist eine Triage-Flaeche: man scrollt durch 3900 Zeilen und
          klappt eine auf, um zu entscheiden, ob es der gesuchte Parameter ist.
          Die vollstaendige Verteilung (bis 25 Eintraege) steht im Detail-View,
          neben den anderen Verteilungen.

          Bei einer stetigen Groesse beantwortet eine Liste der haeufigsten
          Einzelwerte diese Frage ohnehin nicht -- dort stehen Median und
          Quartile. Das Kriterium ist die vorhandene Zahlenstatistik, nicht das
          Aussehen der Werte: `ABP Alarm [Low]` traegt Zahlen als Text *und* hat
          einen Median. */}
      {istNumerisch ? (
        <>
          <Box sx={{ mb: 2 }}>
            <WertekennzahlenCard
              muster={messmuster}
              eingeschraenkt={statistikEingeschraenkt}
              anteilOhneEinheit={anteilOhneEinheit}
            />
          </Box>
          {/* Die Klasseneinteilung (#245). Sie ersetzt die Werteliste, die es
              fuer numerische Parameter nie geben konnte -- bis 1.2 galt eine
              Herzfrequenz mit 125 Messwerten als Kommentarfeld. */}
          {param.valueHistogram?.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <HistogrammCard klassen={param.valueHistogram} />
            </Box>
          )}
        </>
      ) : (
        (param.valueDistribution?.length > 0 || param.valueDistributionReason) && (
          <Box sx={{ mb: 2 }}>
            <WerteverteilungCard
              verteilung={param.valueDistribution}
              grund={param.valueDistributionReason}
              kompakt
            />
          </Box>
        )
      )}

      {/* Units & Data Sources Section */}
      {param.units && param.units.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              p: 1.5,
              backgroundColor: 'white',
              borderRadius: 1,
              boxShadow: 1,
            }}
          >
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: 'text.secondary' }}>
              {t('dataCatalog.expanded.unitsAndSources')}
            </Typography>

            {/* List all units with their sources */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: param.units.length > 1 ? 1.5 : 0 }}>
              {param.units.map((unitInfo, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 0.75,
                    backgroundColor: index === standardIndex ? '#f0f7ff' : '#f5f5f5',
                    borderRadius: 0.75,
                    border: index === standardIndex ? '1px solid #d0e5ff' : '1px solid #e0e0e0'
                  }}
                >
                  {/* Unit */}
                  <Box
                    sx={{
                      minWidth: 70,
                      textAlign: 'center',
                      px: 1,
                      py: 0.25,
                      backgroundColor: 'white',
                      borderRadius: 0.5,
                      border: '1px solid #d0d0d0'
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: unitInfo.unit == null ? '0.7rem' : '0.85rem',
                        fontStyle: unitInfo.unit == null ? 'italic' : 'normal',
                        color: unitInfo.unit == null ? 'text.secondary' : 'text.primary',
                      }}
                    >
                      {/* `unit === null` ist im Contract die nicht erfasste Einheit,
                          von einer echten Auspraegung optisch nicht zu unterscheiden --
                          bis hierher (#264). Dieselbe Loesung wie fuer die
                          Werteverteilung (`valueDistributionEmpty`, #215). */}
                      {unitInfo.unit == null ? t('dataCatalog.expanded.unitNotRecorded') : unitInfo.unit}
                    </Typography>
                  </Box>

                  {/* Herkunft oder Anzahl.

                      Die Seite war gegen `src/data/parameters.json` gebaut, und
                      dort trug jede Einheit eine Liste `sources`. Der Contract
                      (profile_bundle, #202) liefert statt dessen `count` -- die
                      Zahl der Messungen mit dieser Einheit. Ein blosses
                      `unitInfo.sources.join(', ')` warf deshalb bei den ersten
                      echten Daten einen TypeError und riss den ganzen
                      aufgeklappten Bereich mit. Beide Formen werden jetzt
                      angezeigt, keine wird vorausgesetzt. */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.primary" sx={{ fontSize: '0.8rem' }}>
                      {Array.isArray(unitInfo.sources) && unitInfo.sources.length > 0
                        ? unitInfo.sources.join(', ')
                        : Number.isFinite(unitInfo.count)
                          ? t('dataCatalog.expanded.unitMeasurements', { count: unitInfo.count })
                          : ''}
                    </Typography>
                  </Box>

                  {/* Percentage -- `!= null` statt truthy: 0 % ist eine Aussage,
                      keine fehlende Angabe. */}
                  {unitInfo.percentage != null && (
                    <Chip
                      label={`${formatiereProzent(unitInfo.percentage)} %`}
                      size="small"
                      color={index === standardIndex ? 'primary' : 'default'}
                      sx={{ fontWeight: 600, height: 20, fontSize: '0.7rem' }}
                    />
                  )}

                  {/* Primary indicator -- nie auf der nicht erfassten Einheit
                      (index === standardIndex ist -1, wenn ausschliesslich
                      `unit: null` vorliegt; dann zeigt keine Zeile "Standard"). */}
                  {index === standardIndex && (
                    <Chip
                      label={t('dataCatalog.expanded.standard')}
                      size="small"
                      color="success"
                      sx={{ fontWeight: 600, height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
              ))}
            </Box>

            {/* OMOP normalization info -- nur, wenn es mindestens zwei
                *erfasste* Einheiten gibt, zwischen denen ueberhaupt
                normalisiert wird. Bei ausschliesslich `unit: null`
                (standardIndex === -1) gaebe es sonst einen Hinweis mit
                leerem `{{unit}}` -- eine Aussage ueber ein
                Normalisierungsziel, das niemand kennt. Bei genau einer
                erfassten Einheit plus einem nicht erfassten Anteil (z. B.
                `['kg', null]`) gaebe es einen Hinweis auf eine Normalisierung,
                die gar nicht stattfindet -- der nicht erfasste Anteil wird
                bereits ueber `anteilOhneEinheit`/AK2 gekennzeichnet. */}
            {erfassteEinheitenAnzahl > 1 && standardIndex !== -1 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'start',
                  gap: 0.75,
                  p: 1,
                  backgroundColor: '#fff8e1',
                  borderRadius: 0.75,
                  border: '1px solid #ffecb3'
                }}
              >
                <InfoIcon color="warning" fontSize="small" sx={{ mt: 0.1 }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  <strong>{t('dataCatalog.expanded.noteLabel')}</strong>{' '}
                  {t('dataCatalog.expanded.normalizationNotice', { unit: param.units[standardIndex].unit })}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* **Die aufgeklappte Karte zeigt immer beide Zuordnungen** (#788).

          Bis hierher folgte sie dem Umschalter: in Stellung openEHR stand der
          OMOP-Abschnitt nicht mehr da. Das war falsch gedacht. Der Umschalter
          entscheidet, wonach die **Liste** aussieht und gefiltert wird -- eine
          Frage der Spaltenbreite und der Treffermenge. Die aufgeklappte Karte
          ist dagegen der Ort, an dem man **nachsieht**, und dort will man
          beides nebeneinander: was OMOP von diesem Parameter weiss und was
          openEHR. Gerade der Vergleich ist der Grund, warum es zwei
          Darstellungen gibt.

          Es kostet auch nichts: die Karte ist ohnehin aufgeklappt, und ein
          leerer Abschnitt sagt "hier ist nichts zugeordnet" -- eine Auskunft,
          kein Platzverlust. */}
      <>
      {/* OMOP Mapping Section */}
      <Box
        sx={{
          mt: 3,
          p: 2,
          backgroundColor: 'white',
          borderRadius: 1,
          boxShadow: 1,
        }}
      >
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, color: 'text.secondary' }}>
          {t('dataCatalog.expanded.omopMapping')}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          {/* Status Chip */}
          <Chip
            label={getOMOPStatusLabel(param.omopStatus, t)}
            color={getOMOPStatusColor(param.omopStatus)}
            size="small"
            sx={{ fontWeight: 600 }}
          />

          {/* Mapping Details */}
          {param.omopMapping?.topMatch ? (
            <>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 1.5,
                py: 0.75,
                backgroundColor: '#f0f7ff',
                borderRadius: 1,
                border: '1px solid #d0e5ff',
                flex: 1,
                minWidth: 'fit-content'
              }}>
                {/* Concept Name */}
                <Typography variant="body2" fontWeight="bold" color="primary.main">
                  {param.omopMapping.topMatch.conceptName}
                </Typography>

                {/* Vocabulary */}
                <Chip
                  label={param.omopMapping.topMatch.vocabulary}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />

                {/* Concept Code (e.g., LOINC Code) */}
                {param.omopMapping.topMatch.conceptCode && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" color="textSecondary" fontWeight="bold">
                      {t('dataCatalog.expanded.codeLabel')}
                    </Typography>
                    <Typography variant="body2" color="text.primary" sx={{
                      fontFamily: 'monospace',
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      px: 1,
                      py: 0.25,
                      borderRadius: 0.5,
                      fontWeight: 600
                    }}>
                      {param.omopMapping.topMatch.conceptCode}
                    </Typography>
                  </Box>
                )}

                {/* Domain */}
                {param.omopMapping.topMatch.domain && (
                  <Chip
                    label={param.omopMapping.topMatch.domain}
                    size="small"
                    color="default"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                )}

                {/* Concept ID */}
                <Typography variant="caption" color="textSecondary" sx={{
                  borderLeft: '1px solid #d0d0d0',
                  pl: 1.5,
                  fontFamily: 'monospace'
                }}>
                  {t('dataCatalog.expanded.omopId', { id: param.omopMapping.topMatch.conceptId })}
                </Typography>
              </Box>

              {/* ATHENA Link */}
              <Button
                variant="outlined"
                size="small"
                href={`https://athena.ohdsi.org/search-terms/terms/${param.omopMapping.topMatch.conceptId}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ textTransform: 'none' }}
              >
                {t('dataCatalog.expanded.athenaOpen')}
              </Button>
            </>
          ) : param.omopCode ? (
            <>
              <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'monospace' }}>
                ID: {param.omopCode}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                href={`https://athena.ohdsi.org/search-terms/terms/${param.omopCode}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ textTransform: 'none' }}
              >
                {t('dataCatalog.expanded.athenaOpen')}
              </Button>
            </>
          ) : (
            <Typography variant="body2" color="textSecondary" fontStyle="italic">
              {t('dataCatalog.expanded.noMapping')}
            </Typography>
          )}
        </Box>
      </Box>

      </>

      <OpenEhrAbschnitt param={param} t={t} />

      {/* Actions Bar */}
      <Box
        sx={{
          mt: 2,
          display: 'flex',
          gap: 1,
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
        }}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={
            activeWorkingList ? (
              <PlaylistAddCheckIcon />
            ) : (
              <PlaylistAddIcon />
            )
          }
          onClick={e => handleOpenListDialog(param.id, e)}
        >
          {activeWorkingList && parameterLists ? t('dataCatalog.expanded.addToList') : t('dataCatalog.expanded.list')}
        </Button>
        <Button
          variant="contained"
          size="small"
          component={Link}
          to={`/parameter/${getParameterLookupId(param)}`}
          endIcon={<OpenInNewIcon />}
        >
          {t('dataCatalog.expanded.allDetails')}
        </Button>
      </Box>
    </Box>
  );
};

export default ExpandedRow;
